import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Button, Typography, Tag, List, Collapse, Spin, message, Alert } from 'antd';
import {
  CheckCircleOutlined, CrownOutlined, StarOutlined, RocketOutlined,
  ThunderboltOutlined, GlobalOutlined, EyeOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../store/LanguageContext';
import { orderAPI } from '../services/api';
import { PlanItem } from '../types';
import { SUBMIT_PAYMENT_ENABLED } from '../utils/site';

const { Title, Paragraph, Text } = Typography;

const goPaidOrSubmit = (navigate: (path: string) => void, planCode: string, isZh: boolean) => {
  if (SUBMIT_PAYMENT_ENABLED) {
    navigate(`/checkout?plan=${planCode}`);
  } else {
    message.info(isZh ? '付费套餐暂未开放，请走免费提交，我们人工审核上架。' : 'Paid plans are unavailable. Please use free submit for manual review.');
    navigate('/submit');
  }
};

/* ====== Global / English version — streamlined 3-tier pricing ====== */
const GlobalPricingPage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    orderAPI.getPlans()
      .then((res) => setPlans(res.data.plans || []))
      .catch(() => message.error(t('common.operationFailed')))
      .finally(() => setLoading(false));
  }, []);

  const standardPlan = plans.find((p) => p.code === 'standard');
  const premiumPlan = plans.find((p) => p.code === 'premium');

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  const freeFeatures = [
    'Basic tool listing in directory',
    'Standard category inclusion',
    'Searchable by all users',
    'Review in standard queue',
  ];

  const fastFeatures = [
    'Priority review within 24–48 hours',
    'Faster publishing to directory',
    'Better visibility in search & category',
    'Verified badge on your listing',
  ];

  const featuredFeatures = [
    'Homepage featured placement for 7 days',
    'Higher ranking in category & search',
    'Faster review and publishing',
    'Premium visibility badge',
    'Dofollow backlink',
  ];

  const whyItems = [
    { icon: <GlobalOutlined style={{ fontSize: 20, color: '#1677ff' }} />, text: 'Reach early AI adopters globally' },
    { icon: <RocketOutlined style={{ fontSize: 20, color: '#722ed1' }} />, text: 'Faster product discovery' },
    { icon: <EyeOutlined style={{ fontSize: 20, color: '#fa8c16' }} />, text: 'Better launch visibility' },
    { icon: <SafetyCertificateOutlined style={{ fontSize: 20, color: '#52c41a' }} />, text: 'Flexible listing options' },
  ];

  const faqItems = SUBMIT_PAYMENT_ENABLED
    ? [
        {
          q: 'How soon will my tool go live?',
          a: 'Free listing: 3–5 business days. Fast Listing: within 24–48 hours. Homepage Featured: same-day review.',
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept PayPal for international payments. Contact pxy418@gmail.com for other options.',
        },
        {
          q: 'Can I get a refund?',
          a: 'Full refund if your tool doesn\'t pass review. Pro-rated refund for active benefits within the first 48 hours.',
        },
      ]
    : [
        {
          q: 'How soon will my tool go live?',
          a: 'Free submissions are usually reviewed within several business days, depending on queue volume.',
        },
        {
          q: 'Are paid plans available?',
          a: 'Not currently. Free listing only — submit and we review manually. Paid plans return after payment setup is ready.',
        },
        {
          q: 'Will my free listing stay in the directory?',
          a: 'Yes. Once approved, your tool stays in the directory.',
        },
      ];

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '48px 16px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <Title level={2} style={{ marginBottom: 8 }}>
          Submit Your AI Tool
        </Title>
        <Paragraph type="secondary" style={{ fontSize: 16, maxWidth: 520, margin: '0 auto' }}>
          Get discovered by AI tool users, founders, and early adopters.
        </Paragraph>
      </div>

      {!SUBMIT_PAYMENT_ENABLED && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}
          message="Free listing only"
          description="Paid plans and online payment are temporarily unavailable. Please submit for free — we review and list manually."
        />
      )}

      {/* 3 Pricing Cards */}
      <Row gutter={[24, 24]} justify="center" align="stretch">
        {/* === Free Listing === */}
        <Col xs={24} sm={24} md={8}>
          <Card
            hoverable
            style={{
              height: '100%',
              borderRadius: 16,
              border: '1px solid #f0f0f0',
              background: '#fafafa',
            }}
            bodyStyle={{ padding: 28 }}
          >
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <CheckCircleOutlined style={{ fontSize: 36, color: '#8c8c8c' }} />
              <Title level={4} style={{ marginTop: 12, marginBottom: 4, color: '#595959' }}>
                Free Listing
              </Title>
              <Title level={2} style={{ margin: 0, color: '#8c8c8c' }}>
                $0
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>Basic directory inclusion</Text>
            </div>
            <List
              size="small"
              dataSource={freeFeatures}
              renderItem={(item) => (
                <List.Item style={{ border: 'none', padding: '5px 0', color: '#595959' }}>
                  <CheckCircleOutlined style={{ color: '#bfbfbf', marginRight: 8 }} />
                  {item}
                </List.Item>
              )}
            />
            <Button
              block
              size="large"
              style={{ marginTop: 20, borderRadius: 8 }}
              onClick={() => navigate('/submit')}
            >
              Apply for Free Listing
            </Button>
          </Card>
        </Col>

        {SUBMIT_PAYMENT_ENABLED && (
        <>
        {/* === Fast Listing — MAIN CTA === */}
        <Col xs={24} sm={24} md={8}>
          <Card
            hoverable
            style={{
              height: '100%',
              borderRadius: 16,
              border: '2px solid #1677ff',
              boxShadow: '0 8px 32px rgba(22, 119, 255, 0.15)',
              position: 'relative',
            }}
            bodyStyle={{ padding: 28 }}
          >
            <Tag
              color="blue"
              style={{
                position: 'absolute',
                top: -14,
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: 13,
                padding: '2px 16px',
                borderRadius: 20,
                fontWeight: 600,
              }}
            >
              Most Popular
            </Tag>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <ThunderboltOutlined style={{ fontSize: 36, color: '#1677ff' }} />
              <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
                Fast Listing
              </Title>
              <Title level={2} style={{ margin: 0, color: '#1677ff' }}>
                ${standardPlan ? standardPlan.amount : 19}
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>One-time fee</Text>
            </div>
            <List
              size="small"
              dataSource={fastFeatures}
              renderItem={(item) => (
                <List.Item style={{ border: 'none', padding: '5px 0' }}>
                  <CheckCircleOutlined style={{ color: '#1677ff', marginRight: 8 }} />
                  {item}
                </List.Item>
              )}
            />
            <Button
              type="primary"
              block
              size="large"
              style={{
                marginTop: 20,
                borderRadius: 8,
                height: 44,
                fontWeight: 600,
                fontSize: 15,
              }}
              onClick={() => goPaidOrSubmit(navigate, 'standard', language === 'zh-CN')}
            >
              Get Listed Faster
            </Button>
          </Card>
        </Col>

        {/* === Homepage Featured === */}
        <Col xs={24} sm={24} md={8}>
          <Card
            hoverable
            style={{
              height: '100%',
              borderRadius: 16,
              border: '1px solid #f0f0f0',
              background: 'linear-gradient(180deg, #fffbe6 0%, #fff 40%)',
              position: 'relative',
            }}
            bodyStyle={{ padding: 28 }}
          >
            <Tag
              color="gold"
              style={{
                position: 'absolute',
                top: -14,
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: 13,
                padding: '2px 16px',
                borderRadius: 20,
                fontWeight: 600,
              }}
            >
              Best Exposure
            </Tag>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <CrownOutlined style={{ fontSize: 36, color: '#faad14' }} />
              <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
                Homepage Featured
              </Title>
              <div>
                <Title level={2} style={{ margin: 0, color: '#faad14', display: 'inline' }}>
                  ${premiumPlan ? premiumPlan.amount : 79}
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}> / 7 days</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>Maximum visibility</Text>
            </div>
            <List
              size="small"
              dataSource={featuredFeatures}
              renderItem={(item) => (
                <List.Item style={{ border: 'none', padding: '5px 0' }}>
                  <CheckCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                  {item}
                </List.Item>
              )}
            />
            <Button
              block
              size="large"
              style={{
                marginTop: 20,
                borderRadius: 8,
                height: 44,
                fontWeight: 600,
                background: '#faad14',
                borderColor: '#faad14',
                color: '#fff',
              }}
              onClick={() => goPaidOrSubmit(navigate, 'premium', language === 'zh-CN')}
            >
              Get Homepage Exposure
            </Button>
          </Card>
        </Col>
        </>
        )}
      </Row>

      {/* Why list with us */}
      <div style={{
        marginTop: 64,
        padding: '32px 0',
        textAlign: 'center',
      }}>
        <Title level={3} style={{ marginBottom: 32 }}>Why list with us?</Title>
        <Row gutter={[32, 24]} justify="center">
          {whyItems.map((item, idx) => (
            <Col xs={12} sm={6} key={idx}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                {item.icon}
                <Text style={{ fontSize: 14 }}>{item.text}</Text>
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* FAQ */}
      <div style={{ marginTop: 48 }}>
        <Title level={3} style={{ textAlign: 'center', marginBottom: 24 }}>
          FAQ
        </Title>
        <Collapse
          items={faqItems.map((item, idx) => ({
            key: idx,
            label: item.q,
            children: <Paragraph>{item.a}</Paragraph>,
          }))}
          style={{ maxWidth: 700, margin: '0 auto' }}
        />
      </div>
    </div>
  );
};

/* ====== Chinese version — original layout ====== */
const CNPricingPage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    orderAPI.getPlans()
      .then((res) => setPlans(res.data.plans || []))
      .catch(() => message.error(t('common.operationFailed')))
      .finally(() => setLoading(false));
  }, []);

  const iconMap: Record<string, React.ReactNode> = {
    standard: <StarOutlined style={{ fontSize: 32, color: '#1677ff' }} />,
    premium: <CrownOutlined style={{ fontSize: 32, color: '#faad14' }} />,
  };

  const featureKeyMap: Record<string, string> = {
    standard: 'pricing.standardFeatures',
    premium: 'pricing.premiumFeatures',
  };

  const freeFeatures = t('pricing.freeFeatures').split(',');

  const faqItems = [
    { q: t('pricing.faq1q'), a: t('pricing.faq1a') },
    { q: t('pricing.faq2q'), a: t('pricing.faq2a') },
    { q: t('pricing.faq3q'), a: t('pricing.faq3a') },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 16px' }}>
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <Title level={2}>
          <RocketOutlined style={{ marginRight: 8 }} />
          {t('pricing.title')}
        </Title>
        <Paragraph type="secondary" style={{ fontSize: 16 }}>
          {t('pricing.subtitle')}
        </Paragraph>
      </div>

      {!SUBMIT_PAYMENT_ENABLED && (
        <div style={{ maxWidth: 720, margin: '0 auto 32px' }}>
          <Alert
            type="info"
            showIcon
            message="当前仅开放免费上架"
            description="付费套餐、在线支付与对公收款展示暂未开放。请免费提交工具，我们人工审核后上架。"
          />
        </div>
      )}

      <Row gutter={[24, 24]} justify="center">
        {/* 免费方案（静态卡片） */}
        <Col xs={24} sm={12} md={SUBMIT_PAYMENT_ENABLED ? 8 : 12}>
          <Card
            hoverable
            style={{ height: '100%', borderRadius: 12 }}
            bodyStyle={{ padding: 24 }}
          >
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a' }} />
              <Title level={4} style={{ marginTop: 8 }}>
                {t('payment.planFree')}
              </Title>
              <Title level={2} style={{ margin: 0, color: '#52c41a' }}>
                {language === 'zh-CN' ? '¥0' : '$0'}
              </Title>
            </div>
            <List
              size="small"
              dataSource={freeFeatures}
              renderItem={(item) => (
                <List.Item style={{ border: 'none', padding: '6px 0' }}>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  {item}
                </List.Item>
              )}
            />
            <Button block style={{ marginTop: 16 }} type="primary" onClick={() => navigate('/submit')}>
              {SUBMIT_PAYMENT_ENABLED ? t('pricing.currentPlan') : '免费提交工具'}
            </Button>
          </Card>
        </Col>

        {/* 付费套餐：ICP / 支付未开通时不展示 */}
        {SUBMIT_PAYMENT_ENABLED && plans.map((plan) => {
          const features = t(featureKeyMap[plan.code] || 'pricing.standardFeatures').split(',');
          const isPopular = plan.code === 'standard';

          return (
            <Col xs={24} sm={12} md={8} key={plan.id}>
              <Card
                hoverable
                style={{
                  height: '100%',
                  borderRadius: 12,
                  border: isPopular ? '2px solid #1677ff' : undefined,
                }}
                bodyStyle={{ padding: 24, position: 'relative' }}
              >
                {isPopular && (
                  <Tag
                    color="blue"
                    style={{ position: 'absolute', top: -12, right: 16 }}
                  >
                    {t('pricing.popular')}
                  </Tag>
                )}
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  {iconMap[plan.code]}
                  <Title level={4} style={{ marginTop: 8 }}>
                    {plan.name}
                  </Title>
                  <Title level={2} style={{ margin: 0, color: '#1677ff' }}>
                    {plan.display}
                  </Title>
                  {plan.description && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {plan.description}
                    </Text>
                  )}
                </div>
                <List
                  size="small"
                  dataSource={features}
                  renderItem={(item) => (
                    <List.Item style={{ border: 'none', padding: '6px 0' }}>
                      <CheckCircleOutlined style={{ color: '#1677ff', marginRight: 8 }} />
                      {item}
                    </List.Item>
                  )}
                />
                <Button
                  type="primary"
                  block
                  style={{ marginTop: 16 }}
                  onClick={() => goPaidOrSubmit(navigate, plan.code, language === 'zh-CN')}
                >
                  {t('pricing.choosePlan')}
                </Button>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* FAQ 区域 */}
      <div style={{ marginTop: 64 }}>
        <Title level={3} style={{ textAlign: 'center', marginBottom: 24 }}>
          {t('pricing.faq')}
        </Title>
        <Collapse
          items={faqItems.map((item, idx) => ({
            key: idx,
            label: item.q,
            children: <Paragraph>{item.a}</Paragraph>,
          }))}
          style={{ maxWidth: 700, margin: '0 auto' }}
        />
      </div>
    </div>
  );
};

/* ====== Route switcher ====== */
const PricingPage: React.FC = () => {
  const { language } = useLanguage();
  return language === 'en-US' ? <GlobalPricingPage /> : <CNPricingPage />;
};

export default PricingPage;
